'use client'

import { PrismicRichText } from "@prismicio/react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

// Fonction utilitaire pour nettoyer le texte et créer des identifiants
export const sumupTrigger = (text) => {
  return text
    .split(" ")
    .join("-")
    .split("'")
    .join("-")
    .split(":")
    .join("")
    .split("(")
    .join("")
    .split(")")
    .join("")
    .split("--")
    .join("-")
    .split(/\s|&nbsp;/g)
    .join("-");
};

const SideMenu = ({ page, isMobileViewer }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filtrer les slices de contenu d'article
  const richTextSlices = page?.data?.slices?.filter(
    (slice) => slice.slice_type === "article_content" && slice.variation === "default"
  ) || [];

  // Gérer le scroll du body quand le menu est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Observer les éléments pour la navigation
  useEffect(() => {
    const sumUpTriggers = Array.from(document.querySelectorAll('.sumup-trigger'));
    if (sumUpTriggers.length > 0) {
      observeSubMenu(sumUpTriggers);
    }
  }, []);

  // Naviguer vers une section
  const goToSection = (e) => {
    const targetId = e.target.dataset.pointer;
    setIsOpen(false);

    setTimeout(() => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const offsetTop = targetElement.getBoundingClientRect().top -
          document.body.getBoundingClientRect().top - 130;

        window.scrollTo({
          behavior: 'smooth',
          top: offsetTop,
        });
      }
    }, 100);
  };

  // Observer les éléments pour mettre à jour la navigation active
  const observeSubMenu = (elements) => {
    if (isMobileViewer) return;

    const observerOptions = {
      rootMargin: '',
      threshold: 1,

    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Ajouter la classe active à tous les titres
          document.querySelectorAll(".article-sumup-title").forEach(title =>
            title.classList.remove("text-sumup-active")
          );

          // Retirer la classe active du titre correspondant
          const activeTitle = document.querySelector(`[data-pointer="${entry.target.id}"]`);

          if (activeTitle) {
            activeTitle.classList.add("text-sumup-active");
          }

          const dots = document.querySelectorAll(".circle-dots")
          if (dots.length > 0) {
            dots.forEach(dot => dot.classList.add("opacity-0"));
            if (activeTitle.previousElementSibling !== null) {
              activeTitle.previousElementSibling.classList.remove("opacity-0");
            }
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    elements.forEach(element => observer.observe(element));
  };

  // Composant pour les titres H2
  const Heading2Component = ({ text }) => (
    <div className="my-3.5" onClick={goToSection}>
      <h4
        data-pointer={sumupTrigger(text)}
        className="cursor-pointer article-sumup-title font-body"
      >
        {text}
      </h4>
    </div>
  );

  // Composant pour les titres H2 desktop
  const DesktopHeading2Component = ({ text }) => (
    <div className="my-3.5 flex gap-2" onClick={goToSection}>
      <div className="mt-2.5 circle-dots opacity-0">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="4" cy="4" r="4" fill="#F0E500" />
        </svg>
      </div>
      <p
        data-pointer={sumupTrigger(text)}
        className="cursor-pointer article-sumup-title text-sumup no-text-variation"
      >
        {text}
      </p>
    </div>
  );

  return (
    <div className="w-full h-full pointer-events-none lg:pointer-events-auto">
      {/* Overlay mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            className="fixed inset-0 bg-black z-40 cursor-pointer lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Version mobile */}
      <div className="text-dark-green sumup-wrapper lg:hidden sticky top-20 md:top-24 pointer-events-auto z-50 transition-opacity duration-300">
        <div
          className={`flex justify-between items-center bg-light-gray px-5 py-2 transition-all duration-300 ${isOpen ? "rounded-b-0 rounded-t-2xl" : "rounded-2xl"
            }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="h3-sm">Sommaire</span>
          <img
            src="/arrow-down.svg"
            alt="flèche"
            className={`transition-transform mr-2 w-[14px] h-[24px] ${isOpen ? "rotate-0" : "rotate-180"
              }`}
          />
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden bg-white rounded-b-2xl px-5 pb-2"
            >
              {richTextSlices.map((slice, index) => (
                <PrismicRichText
                  key={slice.id ?? index}
                  field={(slice.primary.text || []).filter((el) => el.type === "heading2")}
                  components={{
                    heading2: Heading2Component,
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Version desktop */}
      <div className="sumup-wrapper bg-light-gray p-6 rounded-2xl sticky top-28 mb-10 hidden lg:block text-dark-green">
        <div className="h3-sm text-center">Sommaire</div>
        {richTextSlices.map((slice, index) => (
          <PrismicRichText
            key={slice.id ?? index}
            field={(slice.primary.text || []).filter((el) => el.type === "heading2")}
            components={{
              heading2: DesktopHeading2Component,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SideMenu;
